import {
  Timestamp,
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import { normalizeActivity, removeActivityFromFeedCache } from '../hooks/useFeed';
import type { TonyteActivity } from '../types';
import {
  DRAFT_FIELDS,
  PUBLISHED_FIELDS,
  calendarStatus,
  expandRecurrence,
  missingDetails,
  occurrenceId,
  seriesEditableFields,
  shiftToDate,
  type SeriesRule,
} from '../utils/partnerCalendar';

// In-app Partner Calendar data layer — mirrors the portal's
// Tonyte_website/src/partnerCalendarStore.js on the same docs and rules.
// firestore.rules restrict every write to the event/series owner or an admin;
// the checks here only produce a clear message first.

const ACTIVITIES = 'activities';
const SERIES = 'eventSeries';

type Raw = TonyteActivity & Record<string, any>;

function uid(): string {
  const current = auth.currentUser?.uid;
  if (!current) throw new Error('You are signed out. Sign in again.');
  return current;
}

async function load(eventId: string) {
  const snap = await getDoc(doc(firestore, ACTIVITIES, eventId));
  if (!snap.exists()) throw new Error('This event no longer exists.');
  return { raw: snap.data() as Raw, ref: snap.ref };
}

// The partner's own events (every state), sorted by date. Portal/Luma events
// carry ownerUid; events created in the app's editor only have createdBy/hostId
// (all three are owner-readable in firestore.rules), so the lists are merged.
export async function listPartnerEvents(ownerUid: string): Promise<Raw[]> {
  const snaps = await Promise.all(['ownerUid', 'createdBy', 'hostId'].map(field =>
    getDocs(query(collection(firestore, ACTIVITIES), where(field, '==', ownerUid), limit(500)))));
  const byId = new Map<string, Record<string, any>>();
  snaps.forEach(snap => snap.docs.forEach(d => byId.set(d.id, d.data())));
  return [...byId.entries()]
    .map(([id, data]) => ({ ...normalizeActivity(id, data, ACTIVITIES), ...data, id }) as Raw)
    .filter(e => e.deleted !== true)
    .sort((a, b) => `${a.startDate || ''} ${a.startTime || ''}`.localeCompare(`${b.startDate || ''} ${b.startTime || ''}`));
}

export async function getSeries(seriesId: string): Promise<({ id: string; rule: SeriesRule; startDate: string } & Record<string, any>) | null> {
  const snap = await getDoc(doc(firestore, SERIES, seriesId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as any) : null;
}

export async function publishEvent(eventId: string) {
  const { raw, ref } = await load(eventId);
  if (calendarStatus(raw) === 'past') throw new Error('This event has already ended.');
  if (raw.status === 'cancelled') throw new Error('Restore the event before publishing it.');
  const { required } = missingDetails(raw);
  // Luma drafts additionally need a description and cover (portal rule).
  if (raw.externalSource?.provider === 'luma' && !(raw.coverPhoto || raw.coverImageUrl)) required.push('cover image');
  if (required.length) throw new Error(`Add these details before publishing: ${required.join(', ')}.`);
  await updateDoc(ref, {
    ...PUBLISHED_FIELDS,
    incomplete: false,
    missingFields: [],
    needsRevalidation: false,
    scheduledPublishAt: deleteField(),
    scheduleError: deleteField(),
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function schedulePublish(eventId: string, whenMs: number) {
  const { raw, ref } = await load(eventId);
  if (!Number.isFinite(whenMs) || whenMs <= Date.now()) throw new Error('Pick a time in the future.');
  const { required } = missingDetails(raw);
  if (required.length) throw new Error(`Add these details before scheduling: ${required.join(', ')}.`);
  await updateDoc(ref, { ...DRAFT_FIELDS, scheduledPublishAt: Timestamp.fromMillis(whenMs), scheduleError: deleteField(), updatedAt: serverTimestamp() });
  removeActivityFromFeedCache(eventId);
}

export async function unpublishEvent(eventId: string) {
  const { ref } = await load(eventId);
  await updateDoc(ref, { ...DRAFT_FIELDS, scheduledPublishAt: deleteField(), updatedAt: serverTimestamp() });
  removeActivityFromFeedCache(eventId);
}

export async function cancelEvent(eventId: string) {
  const { raw, ref } = await load(eventId);
  await updateDoc(ref, {
    status: 'cancelled', approved: false, isPublic: false,
    cancelledAt: serverTimestamp(), cancelledBy: 'partner', cancelledByUid: uid(),
    previousPublishStatus: raw.publishStatus || (raw.approved ? 'published' : 'draft'),
    scheduledPublishAt: deleteField(),
    ...(raw.seriesId ? { seriesException: true } : {}),
    updatedAt: serverTimestamp(),
  });
  removeActivityFromFeedCache(eventId);
}

export async function restoreEvent(eventId: string) {
  const { ref } = await load(eventId);
  await updateDoc(ref, { ...DRAFT_FIELDS, cancelledAt: deleteField(), cancelledBy: deleteField(), cancelledByUid: deleteField(), updatedAt: serverTimestamp() });
}

export async function duplicateEvent(eventId: string): Promise<string> {
  const { raw } = await load(eventId);
  const copy: Record<string, any> = { ...raw };
  [
    'id', 'externalSource', 'seriesId', 'seriesException', 'occurrenceDate', 'scheduledPublishAt', 'scheduleError',
    'cancelledAt', 'cancelledBy', 'cancelledByUid', 'previousPublishStatus', 'publishedAt', 'sourceUpdatedAt',
    'isWeeklyFeatured', 'featuredStartAt', 'featuredEndAt', 'featuredPriority', 'featuredBy', 'topPick',
    'partnerId', 'partnerProfileId', 'venueId', 'needsRevalidation', 'startsAt', 'endsAt', 'importSource', 'importBatchId',
  ].forEach(key => { delete copy[key]; });
  const owner = raw.ownerUid || uid();
  const created = await addDoc(collection(firestore, ACTIVITIES), {
    ...copy, ...DRAFT_FIELDS, recurrencePattern: 'none', ownerUid: owner, createdBy: owner, hostId: owner,
    participants: 0, attendees: [], interestedCount: 0, duplicatedFrom: eventId, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return created.id;
}

// Tombstone + delete (the tombstone also stops a Luma sync from re-creating
// an import); a deleted series date is added to the series' skip list.
export async function deleteEvent(eventId: string) {
  const { raw } = await load(eventId);
  await setDoc(doc(firestore, 'deletedActivities', eventId), { deletedBy: uid(), deletedAt: Timestamp.now() }, { merge: true });
  await deleteDoc(doc(firestore, ACTIVITIES, eventId));
  removeActivityFromFeedCache(eventId);
  if (raw.seriesId && raw.occurrenceDate) {
    await updateDoc(doc(firestore, SERIES, raw.seriesId), { 'rule.skipDates': arrayUnion(raw.occurrenceDate), updatedAt: serverTimestamp() }).catch(() => {});
  }
}

export async function detachFromLuma(eventId: string) {
  const { raw, ref } = await load(eventId);
  if (raw.externalSource?.provider !== 'luma') throw new Error('This event is not synced from Luma.');
  await updateDoc(ref, { 'externalSource.detached': true, 'externalSource.detachedAt': Timestamp.now(), updatedAt: serverTimestamp() });
}

async function seriesOccurrences(seriesId: string, owner: string) {
  const snap = await getDocs(query(collection(firestore, ACTIVITIES), where('ownerUid', '==', owner), where('seriesId', '==', seriesId)));
  return snap.docs.map(d => {
    const data = d.data() as Raw;
    return { ...data, id: d.id, ref: d.ref, occurrenceDate: String(data.occurrenceDate || data.startDate || '') };
  });
}

export async function publishSeriesFromDate(eventId: string): Promise<number> {
  const { raw } = await load(eventId);
  if (!raw.seriesId) { await publishEvent(eventId); return 1; }
  const { required } = missingDetails(raw);
  if (required.length) throw new Error(`Add these details before publishing: ${required.join(', ')}.`);
  const from = raw.occurrenceDate || raw.startDate;
  const items = (await seriesOccurrences(raw.seriesId, raw.ownerUid || uid())).filter(o => o.occurrenceDate >= from && o.status !== 'cancelled' && o.publishStatus === 'draft');
  const batch = writeBatch(firestore);
  items.forEach(o => batch.update(o.ref, { ...PUBLISHED_FIELDS, incomplete: false, missingFields: [], scheduledPublishAt: deleteField(), publishedAt: serverTimestamp(), updatedAt: serverTimestamp() }));
  await batch.commit();
  return items.length;
}

export async function cancelSeriesFromDate(eventId: string): Promise<number> {
  const { raw } = await load(eventId);
  if (!raw.seriesId) { await cancelEvent(eventId); return 1; }
  const from = raw.occurrenceDate || raw.startDate;
  const items = (await seriesOccurrences(raw.seriesId, raw.ownerUid || uid())).filter(o => o.occurrenceDate >= from && o.status !== 'cancelled');
  const batch = writeBatch(firestore);
  items.forEach(o => {
    batch.update(o.ref, { status: 'cancelled', approved: false, isPublic: false, cancelledAt: serverTimestamp(), cancelledBy: 'partner', cancelledByUid: uid(), scheduledPublishAt: deleteField(), updatedAt: serverTimestamp() });
    removeActivityFromFeedCache(o.id);
  });
  await batch.commit();
  return items.length;
}

// Used by Create Event: one series doc + one real event doc per date.
export async function createSeries(basePayload: Record<string, any>, rule: SeriesRule): Promise<{ seriesId: string; dates: string[]; firstId: string }> {
  const owner = basePayload.ownerUid || uid();
  const dates = expandRecurrence(rule, basePayload.startDate);
  if (dates.length < 2) throw new Error('This repeat pattern produces only one date — choose more dates or make it a one-time event.');
  const seriesRef = doc(collection(firestore, SERIES));
  const batch = writeBatch(firestore);
  batch.set(seriesRef, {
    ownerUid: owner,
    rule: { pattern: rule.pattern, weekdays: rule.weekdays || [], until: String(rule.until || ''), count: String(rule.count || ''), extraDates: rule.extraDates || [], skipDates: rule.skipDates || [] },
    startDate: basePayload.startDate,
    endDate: basePayload.endDate || basePayload.startDate,
    template: seriesEditableFields(basePayload),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  // startsAt/endsAt describe the first date only — occurrences omit them.
  const occurrenceBase = { ...basePayload };
  delete occurrenceBase.startsAt;
  delete occurrenceBase.endsAt;
  dates.forEach(date => {
    const { occurrenceDate, ...shifted } = shiftToDate(basePayload, date);
    batch.set(doc(firestore, ACTIVITIES, occurrenceId(seriesRef.id, date)), {
      ...occurrenceBase,
      ...shifted,
      occurrenceDate,
      recurrencePattern: 'none',
      recurrenceWeekdays: [],
      recurrenceEndDate: '',
      recurrenceOccurrenceCount: '',
      recurrenceExtraDates: [],
      recurrenceSkipDates: [],
      seriesId: seriesRef.id,
      ownerUid: owner,
    });
  });
  await batch.commit();
  return { seriesId: seriesRef.id, dates, firstId: occurrenceId(seriesRef.id, dates[0]) };
}

// "This and all following dates": content and times go to every following
// date that wasn't edited on its own (dates themselves stay; changing the
// repeat pattern is done in the partner portal).
export async function updateSeriesFromDate(eventId: string, payload: Record<string, any>): Promise<number> {
  const { raw } = await load(eventId);
  if (!raw.seriesId) throw new Error('This event is not part of a series.');
  const from = raw.occurrenceDate || raw.startDate;
  const fields = seriesEditableFields(payload);
  const span = { startDate: payload.startDate || raw.startDate, endDate: payload.endDate || raw.endDate || raw.startDate };
  const items = (await seriesOccurrences(raw.seriesId, raw.ownerUid || uid())).filter(o => o.occurrenceDate >= from && (!o.seriesException || o.id === eventId));
  const batch = writeBatch(firestore);
  items.forEach(o => {
    const { occurrenceDate, ...dates } = shiftToDate(span, o.occurrenceDate);
    batch.update(o.ref, { ...fields, ...dates, occurrenceDate, ...(o.id === eventId ? { seriesException: false } : {}), updatedAt: serverTimestamp() });
  });
  const series = await getSeries(raw.seriesId);
  batch.update(doc(firestore, SERIES, raw.seriesId), { template: { ...(series?.template || {}), ...fields }, updatedAt: serverTimestamp() });
  await batch.commit();
  return items.length;
}
